// Locale → copy for "Upload live from your camera" (/live-uploading): uploading straight from a tethered camera
// into a Photohawk gallery with CloudTether (CloudTether's partner API: photohawk-serverless docs/cloudtether-api.md).
// English (source of truth) and Spanish; any other language falls back to English. This page is NOT one of the
// launch pages (src/content/marketingLangs.test.ts): their copy rules forbid cameras and tethering, which is what
// this page is about.
import { useTranslation } from "react-i18next";
import { baseLang } from "@/lib/seo/site";
import en, { type LiveUploadingCopy } from "./copy.en";
import es from "./copy.es";

import liveShoot320Avif from "@/assets/live-uploading/live-shoot-320.avif";
import liveShoot640Avif from "@/assets/live-uploading/live-shoot-640.avif";
import liveShoot320Webp from "@/assets/live-uploading/live-shoot-320.webp";
import liveShoot640Webp from "@/assets/live-uploading/live-shoot-640.webp";
import liveShootPng from "@/assets/live-uploading/live-shoot-640.png";
import connect320Avif from "@/assets/live-uploading/connect-320.avif";
import connect640Avif from "@/assets/live-uploading/connect-640.avif";
import connect320Webp from "@/assets/live-uploading/connect-320.webp";
import connect640Webp from "@/assets/live-uploading/connect-640.webp";
import connectPng from "@/assets/live-uploading/connect-640.png";
import chooseGallery320Avif from "@/assets/live-uploading/choose-gallery-320.avif";
import chooseGallery640Avif from "@/assets/live-uploading/choose-gallery-640.avif";
import chooseGallery320Webp from "@/assets/live-uploading/choose-gallery-320.webp";
import chooseGallery640Webp from "@/assets/live-uploading/choose-gallery-640.webp";
import chooseGalleryPng from "@/assets/live-uploading/choose-gallery-640.png";
import threeCameras320Avif from "@/assets/live-uploading/three-cameras-320.avif";
import threeCameras640Avif from "@/assets/live-uploading/three-cameras-640.avif";
import threeCameras320Webp from "@/assets/live-uploading/three-cameras-320.webp";
import threeCameras640Webp from "@/assets/live-uploading/three-cameras-640.webp";
import threeCamerasPng from "@/assets/live-uploading/three-cameras-640.png";
import shootSettings320Avif from "@/assets/live-uploading/shoot-settings-320.avif";
import shootSettings640Avif from "@/assets/live-uploading/shoot-settings-640.avif";
import shootSettings320Webp from "@/assets/live-uploading/shoot-settings-320.webp";
import shootSettings640Webp from "@/assets/live-uploading/shoot-settings-640.webp";
import shootSettingsPng from "@/assets/live-uploading/shoot-settings-640.png";
import delivered320Avif from "@/assets/live-uploading/delivered-320.avif";
import delivered640Avif from "@/assets/live-uploading/delivered-640.avif";
import delivered320Webp from "@/assets/live-uploading/delivered-320.webp";
import delivered640Webp from "@/assets/live-uploading/delivered-640.webp";
import deliveredPng from "@/assets/live-uploading/delivered-640.png";
import destinations320Avif from "@/assets/live-uploading/destinations-320.avif";
import destinations640Avif from "@/assets/live-uploading/destinations-640.avif";
import destinations320Webp from "@/assets/live-uploading/destinations-320.webp";
import destinations640Webp from "@/assets/live-uploading/destinations-640.webp";
import destinationsPng from "@/assets/live-uploading/destinations-640.png";

/** Where the page lives on the marketing site (linked from the footers, the help article and the campaign email). */
export const LIVE_UPLOADING_PATH = "/live-uploading";

/** CloudTether's landing page for Photohawk users, tagged per source so CloudTether can tell them apart. */
const cloudTetherUrl = (medium: "website" | "help" | "email") =>
  `https://cloudtether.app/photohawk?utm_source=photohawk&utm_medium=${medium}&utm_campaign=photohawk-launch`;
/** "Get CloudTether" on this page. */
export const CLOUDTETHER_URL = cloudTetherUrl("website");
/** "Get CloudTether" in the help article (the article's Markdown spells it out; kept here for the tests). */
export const CLOUDTETHER_HELP_URL = cloudTetherUrl("help");

/** The help-centre article with the step-by-step setup and troubleshooting. */
export const LIVE_UPLOADING_HELP_SLUG = "upload-live-with-cloudtether";
export const LIVE_UPLOADING_HELP_PATH = `/help/articles/${LIVE_UPLOADING_HELP_SLUG}`;

/**
 * CloudTether's own screens (iPhone, light theme, demo data; supplied by CloudTether, 30 Oct 2026), each at 320 and
 * 640 px wide in AVIF and WebP with a 640 px PNG fallback. Sources: 1320×2868; made by scratchpad image script.
 */
export interface Shot {
  id: "live-shoot" | "connect" | "choose-gallery" | "three-cameras" | "shoot-settings" | "delivered" | "destinations";
  width: number;
  height: number;
  avif: string;
  webp: string;
  png: string;
}

const shot = (id: Shot["id"], a320: string, a640: string, w320: string, w640: string, png: string): Shot => ({
  id,
  width: 640,
  height: 1391,
  avif: `${a320} 320w, ${a640} 640w`,
  webp: `${w320} 320w, ${w640} 640w`,
  png,
});

export const SHOTS: Record<Shot["id"], Shot> = {
  "live-shoot": shot("live-shoot", liveShoot320Avif, liveShoot640Avif, liveShoot320Webp, liveShoot640Webp, liveShootPng),
  connect: shot("connect", connect320Avif, connect640Avif, connect320Webp, connect640Webp, connectPng),
  "choose-gallery": shot("choose-gallery", chooseGallery320Avif, chooseGallery640Avif, chooseGallery320Webp, chooseGallery640Webp, chooseGalleryPng),
  "three-cameras": shot("three-cameras", threeCameras320Avif, threeCameras640Avif, threeCameras320Webp, threeCameras640Webp, threeCamerasPng),
  "shoot-settings": shot("shoot-settings", shootSettings320Avif, shootSettings640Avif, shootSettings320Webp, shootSettings640Webp, shootSettingsPng),
  delivered: shot("delivered", delivered320Avif, delivered640Avif, delivered320Webp, delivered640Webp, deliveredPng),
  destinations: shot("destinations", destinations320Avif, destinations640Avif, destinations320Webp, destinations640Webp, destinationsPng),
};

const COPY_BY_LANG: Record<string, LiveUploadingCopy> = { en, es };

export const getLiveUploadingCopy = (lang: string | undefined): LiveUploadingCopy => COPY_BY_LANG[baseLang(lang)] ?? en;

export function useLiveUploadingCopy(): { copy: LiveUploadingCopy; lang: string } {
  const { i18n } = useTranslation();
  const base = baseLang(i18n.language);
  const lang = COPY_BY_LANG[base] ? base : "en";
  return { copy: COPY_BY_LANG[lang], lang };
}

export type { LiveUploadingCopy };
